/**
 * Sync Payload Schemas — the server→browser events around message sync that
 * had no contract (s2-07d, tracker W2.09): message:sync, message:gap_warning,
 * user_message:broadcast.
 *
 * THE WIRE IS THE TRUTH. Fields are transcribed from the emitters named on each
 * schema (apps/server at origin/staging, 2026-09-03). The browser's inline
 * types read one field none of these emitters send (message:sync `accessLevel`)
 * — listed in PR s2-07d-b, not declared here.
 */

import { z } from 'zod';
import { HARNESS_IDS } from '../harness/ids.js';
import { CHANNEL_TYPES } from '../types/channel.js';
import { TaskAppSchema, TaskOutputSchema, TaskTodoGroupSchema } from './task-payloads.schema.js';

/**
 * One transcript message on the sync wire — MessageSyncMessage in
 * apps/server/src/handlers/socket/message-sync-payload.ts. `timestamp` is a
 * Date on the server object and an ISO string once socket.io serializes it;
 * both are accepted so the schema is true on either side of the socket. Content
 * blocks are validated by their discriminator only: the ContentBlock union is
 * one variant short of the type (checkpoint — review finding 2) and a sync must
 * never lose a transcript over a block it does not know.
 */
export const MessageSyncMessageSchema = z.object({
  id: z.string().min(1),
  role: z.enum(['user', 'assistant']),
  content: z.array(z.object({ type: z.string().min(1) }).passthrough()),
  timestamp: z.union([z.string(), z.date()]),
  agentSlug: z.string(),
  sdkMessageUuid: z.string().optional(),
  seq: z.number().optional(),
});

/**
 * message:sync — apps/server/src/handlers/socket/message-sync-payload.ts
 * (buildMessageSyncPayload → session-handlers.ts:327 / :345 and the reattach
 * catch-up in services/relay/replay-catchup.ts:188, which adds `catchUp: true`).
 * The refusal shape ({ taskId, messages: [], error, errorCode }) and the
 * load-failure shape ({ taskId, messages: [], timestamp, error }) are the same
 * object with the optionals absent.
 */
export const MessageSyncPayloadSchema = z.object({
  taskId: z.string().min(1),
  messages: z.array(MessageSyncMessageSchema),
  timestamp: z.string().optional(),
  title: z.string().optional(),
  runtime: z.enum(HARNESS_IDS).optional(),
  model: z.string().optional(),
  lifecycleState: z.string().optional(),
  seq: z.number().optional(),
  pendingInteraction: z.record(z.string(), z.unknown()).nullable().optional(),
  pendingInteractions: z.array(z.record(z.string(), z.unknown())).optional(),
  todos: z.array(TaskTodoGroupSchema).optional(),
  outputs: z.array(TaskOutputSchema).optional(),
  /** LivePreviewPointer | null — validated loosely (an object) so the pointer shape can grow. */
  livePreview: z.object({}).passthrough().nullable().optional(),
  apps: z.array(TaskAppSchema).optional(),
  originChannel: z.enum(CHANNEL_TYPES).nullable().optional(),
  error: z.string().optional(),
  errorCode: z.string().optional(),
  catchUp: z.boolean().optional(),
});

/**
 * message:gap_warning — session-handlers.ts:336 and task-handlers.ts:124:
 * `{ taskId, ...ReconciliationResult, timestamp }` where ReconciliationResult is
 * { inMemoryCount, dbCount, hasGap, gapDescription? } (message.service.ts).
 */
export const MessageGapWarningPayloadSchema = z.object({
  taskId: z.string().min(1),
  inMemoryCount: z.number(),
  dbCount: z.number(),
  hasGap: z.boolean(),
  gapDescription: z.string().optional(),
  timestamp: z.string(),
});

/**
 * user_message:broadcast — apps/server/src/handlers/socket/user-message-drive.ts:179:
 * the other tabs of the same user see the message this tab just sent.
 */
export const UserMessageBroadcastPayloadSchema = z.object({
  taskId: z.string().min(1),
  messageId: z.string().min(1),
  message: z.string(),
  timestamp: z.string(),
});

export type MessageSyncMessage = z.infer<typeof MessageSyncMessageSchema>;
export type MessageSyncWirePayload = z.infer<typeof MessageSyncPayloadSchema>;
export type MessageGapWarningPayload = z.infer<typeof MessageGapWarningPayloadSchema>;
export type UserMessageBroadcastPayload = z.infer<typeof UserMessageBroadcastPayloadSchema>;
